import { useState, useEffect } from 'react';
import GeneralModal from "../../../../components/GeneralModal/GeneralModal";
import Select from 'react-select';
import './EquipmentTransferModalStyles.scss';
import { useTransferEquipmentMutation, useLazyGetAgencyQuery } from '../../../../store/builder/inspections.api';
import { toast } from 'react-toastify';
import { useTranslation } from '../../../../hooks/useTranslation';
import { useTransferFacilities } from '../../../../hooks/useFacilities';
const EquipmentTransferModal = ({ equipment, setIsOpen, getEquipmentsInitial }) => {
  const {t} = useTranslation();
  const [step, setStep] = useState('confirm');
  const [formData, setFormData] = useState({
    selectedFBO: '',
    openInspectionsOption: 'convert',
    correctiveActionsOption: 'transfer',
    returnToServiceOption: 'transfer',
    selectedFBORevision: '',
    selectedFacility: ''
  });
  const [transferEquipment, { isLoading: isTransferLoading }] = useTransferEquipmentMutation();

  const handleFirstConfirm = () => {
    setStep('form');
  };

  const handleSecondConfirm = async () => {
      transferEquipment({
      id: equipment?.id, body: 
      {
        agency_id: formData.selectedFBO, //destination agency
        transfer_corrective_actions : formData.correctiveActionsOption === 'transfer',
        transfer_rts : formData.returnToServiceOption === 'transfer',
        transfer_open_as_rts: formData.openInspectionsOption === 'convert',
        facility_id: formData.selectedFacility
        }
      })
        .then((res) => {
          if (res.error) {
            toast.error(res.error.data.message);
          }
          if (res.data.message) {
            toast.success(res.data.message);
            setIsOpen(false);
            setStep('confirm');
            getEquipmentsInitial();
          }
      })
  };

  const handleCancel = () => {
    setIsOpen(false);
    setStep('confirm');
    setFormData({
      selectedFBO: '',
      openInspectionsOption: 'convert',
      correctiveActionsOption: 'transfer',
      returnToServiceOption: 'transfer'
    });
  };

  const getModalContent = () => {
    if (step === 'confirm') {
      return <ConfirmContent t={t} />;
    } else if (step === 'form') {
      return <TransferForm formData={formData} setFormData={setFormData} equipment={equipment} />;
    }
  };

  const getModalProps = () => {
    if (step === 'confirm') {
      return {
        title: t('equipmentTable.modals.transfer.title'),
        onSubmit: handleFirstConfirm,
        onClose: handleCancel,
      };
    } else if (step === 'form') {
      return {
        title: t('equipmentTable.modals.transfer.title'),
        onSubmit: handleSecondConfirm,
        onClose: handleCancel,
        disableSubmit: !formData.selectedFBO || isTransferLoading || !formData.selectedFacility,
        btnType: !formData.selectedFBO || isTransferLoading ? 'disabled' : 'save'
      };
    }
  };

  const modalProps = getModalProps();

  return (  
    <>
        <GeneralModal 
            {...modalProps}
            cancelTitle={t('equipmentTable.modals.transfer.cancel')}
            submitTitle={t('equipmentTable.modals.transfer.yesContinue')}
          >
              {getModalContent()}
          </GeneralModal>
    </>
  );
};

export default EquipmentTransferModal;

const ConfirmContent = ({ t }) => {
  return (
    <div className="content">
      <div className="content__image">
        <img src="/assets/transfer-confirm.png" alt="equipment-transfer-modal" />
      </div>
      <div className="content__text">
        <p>{t('equipmentTable.modals.transfer.confirmMessage')}</p>
        <p>{t('equipmentTable.modals.transfer.confirmDescription')}</p>
      </div>
    </div>
  );
};

const TransferForm = ({ formData, setFormData, equipment }) => {
  const [
    getAgencies,
    {
      data: agencyData,
      isLoading: isLoadingAgencies,
      error: agencyError,
    },
  ] = useLazyGetAgencyQuery();

  const { data: facilitiesData, isFetching: isLoadingFacilities, refetch: refetchFacilities } = useTransferFacilities({
    agency_id: formData.selectedFBO,
    enabled: !!formData.selectedFBO,
    onSuccess: (data) => {
    },
    onError: (error) => {
    },
  });

  const currentFbo = localStorage.getItem('current-fbo');
  const {t} = useTranslation();
  const isEquipmentFueling = equipment?.default_type === "fueling";
  // hasOldLocation is array of object with agency and facility
  const oldLocations = equipment?.original_location;
  const oldLocationChoosen = oldLocations ? oldLocations.find(location => location.agency.id === formData.selectedFBO) : null;
  const oldFbo = oldLocationChoosen ? oldLocationChoosen.agency : null;
  const oldFacility = oldLocationChoosen ? oldLocationChoosen.facility : null;
  useEffect(() => {
    getAgencies();
  }, [getAgencies]);

  // Auto-select old FBO if it exists
  useEffect(() => {
    if (oldFbo && agencyData && !formData.selectedFBO) {
      const oldFboOption = agencyData.find(agency => agency.id === oldFbo.id);
      if (oldFboOption) {
        setFormData({
          ...formData,
          selectedFBO: oldFbo.id,
          selectedFBORevision: oldFboOption.revision_code,
        });
      }
    }
  }, [agencyData, oldFbo]);

  useEffect(() => {
    if (formData.selectedFBO) {
      refetchFacilities();
    } else {
      setFormData({
        ...formData,
        selectedFacility: '',
      });
    }
  }, [formData.selectedFBO]);

  const fboOptions = () => {
    if (isLoadingAgencies) {  
      return [];
    }
    if (agencyData?.length === 0) {
      return [];
    }
    if (agencyError) {
      return [];
    }
    
    return agencyData?.filter(agency => agency.id !== +currentFbo)?.map(agency => ({
      value: agency.id,
      label: agency.title,
      revision_code: agency.revision_code
    }));
  };

  const handleFBOChange = (selectedOption) => {
    setFormData({
      ...formData, 
      selectedFBO: selectedOption ? selectedOption.value : '',
      selectedFBORevision: selectedOption ? selectedOption.revision_code : '',
      openInspectionsOption: 'convert',
      correctiveActionsOption: 'transfer',
      returnToServiceOption: 'transfer'
    });
  };

  const handleFacilityChange = (selectedOption) => {
    setFormData({
      ...formData, 
      selectedFacility: selectedOption ? selectedOption.value : '',
    });
  };

  const getSelectedValue = () => {
    if (!formData.selectedFBO) return null;
    const option = fboOptions().find(opt => opt.value === formData.selectedFBO);
    return option || null;
  };

  const getSelectedFacilityValue = () => {
    if (!formData.selectedFacility) return null;
    const option = facilityOptions().find(opt => opt.value === formData.selectedFacility);
    return option || null;
  };

  const facilityOptions = () => {
    if (!facilitiesData) return [];
    
    let filteredFacilities = facilitiesData?.filter(facility => (
      isEquipmentFueling || facility.default_type === ""
    ));
    
    // If old FBO is selected, show only the old facility as an option
    if (formData.selectedFBO && oldFbo && formData.selectedFBO === oldFbo.id && oldFacility) {
      filteredFacilities = facilitiesData?.filter(facility => facility.id === oldFacility.id);
    }
    
    return filteredFacilities?.map(facility => ({
      value: facility.id,
      label: facility.name,
    }));
  };

  useEffect(() => {
    if (isEquipmentFueling && formData.selectedFBO) {
      setFormData({
        ...formData,
        selectedFacility: facilitiesData?.find(facility => facility.default_type === "fueling")?.id,
      });
    } else if (formData.selectedFBO && oldFbo && formData.selectedFBO === oldFbo.id && oldFacility && facilitiesData) {
      // Auto-select old facility when old FBO is selected
      const oldFacilityOption = facilitiesData.find(facility => facility.id === oldFacility.id);
      if (oldFacilityOption) {
        setFormData({
          ...formData,
          selectedFacility: oldFacility.id,
        });
      }
    }
  }, [facilitiesData, formData.selectedFBO, oldFbo, oldFacility]);

  return (
    <div className="transfer-form">
      <div className="transfer-form__section">
        <h3 className="transfer-form__section-title">{t('equipmentTable.headers.toFbo')}</h3>
        <div className="transfer-form__fbo-selector">
          <Select
            value={getSelectedValue()}
            onChange={handleFBOChange}
            options={fboOptions()}
            placeholder={t('equipmentTable.modals.transfer.selectDestinationFbo')}
            isClearable
            isSearchable
            isLoading={isLoadingAgencies}
            noOptionsMessage={() => t('equipmentTable.modals.transfer.noFbosAvailable')}
            styles={{
              control: (baseStyles, state) => ({
                ...baseStyles,
                borderColor: state.isFocused ? '#E9EAEB' : '#E9EAEB',
                borderRadius: 8,
                height: 42,
                backgroundColor: '#F4F5F6',
                border: '1px solid #e9eaeb',
                boxShadow: 'none',
                '&:hover': {
                  borderColor: '#E9EAEB',
                },
              }),
              menu: (baseStyles) => ({
                ...baseStyles,
                zIndex: 9999,
              }),
              option: (baseStyles, state) => ({
                ...baseStyles,
                backgroundColor: state.isSelected ? '#4369b0' : state.isFocused ? '#f4f5f6' : 'white',
                color: state.isSelected ? 'white' : '#27323c',
                '&:hover': {
                  backgroundColor: state.isSelected ? '#4369b0' : '#f4f5f6',
                },
              }),
              placeholder: (baseStyles) => ({
                ...baseStyles,
                color: '#999999',
              }),
            }}
          />
          <br />
          <h3 className="transfer-form__section-title">{t('equipmentTable.modals.transfer.toFacility')}</h3>
          <Select
            value={getSelectedFacilityValue()}
            onChange={handleFacilityChange}
            options={facilityOptions()}
            placeholder={t('equipmentTable.modals.transfer.selectDestinationFacility')}
            isClearable
            isSearchable
            isLoading={isLoadingFacilities}
            noOptionsMessage={()=>t('equipmentTable.modals.transfer.noFacilitiesAvailable')}
            isDisabled={isEquipmentFueling}
            styles={{
              control: (baseStyles, state) => ({
                ...baseStyles,
                borderColor: state.isFocused ? '#E9EAEB' : '#E9EAEB',
                borderRadius: 8,
                height: 42,
                backgroundColor: '#F4F5F6',
                border: '1px solid #e9eaeb',
                boxShadow: 'none',
                '&:hover': {
                  borderColor: '#E9EAEB',
                },
              }),
              menu: (baseStyles) => ({
                ...baseStyles,
                zIndex: 9999,
              }),
              option: (baseStyles, state) => ({
                ...baseStyles,
                backgroundColor: state.isSelected ? '#4369b0' : state.isFocused ? '#f4f5f6' : 'white',
                color: state.isSelected ? 'white' : '#27323c',
                '&:hover': {
                  backgroundColor: state.isSelected ? '#4369b0' : '#f4f5f6',
                },
              }),
              placeholder: (baseStyles) => ({
                ...baseStyles,
                color: '#999999',
              }),
            }}
          />
        </div>
      </div>

      {/* Show forms when no FBO is selected OR when FBO is selected and revision codes match */}
      {(!formData?.selectedFBO || formData?.selectedFBORevision === equipment?.revision_code) && (
        <>
          <div className="transfer-form__section">
            <h3 className="transfer-form__section-title">{t('equipmentTable.modals.transfer.equipmentHasCorrectiveActions')}</h3>
            <div className="transfer-form__radio-group">
              <label className="transfer-form__radio-label">
                <input 
                  type="radio" 
                  name="correctiveActions" 
                  value="transfer" 
                  checked={formData.correctiveActionsOption === 'transfer'}
                  onChange={() => setFormData({...formData, correctiveActionsOption: 'transfer'})}
                  className="transfer-form__radio"
                />
                <span className="transfer-form__radio-custom"></span>
                {t('equipmentTable.modals.transfer.transferAllOpenCorrectiveActions')}
              </label>
            </div>
          </div>

          <div className="transfer-form__section">
            <h3 className="transfer-form__section-title">{t('equipmentTable.modals.transfer.equipmentHasReturnToServiceInspections')}</h3>
            <div className="transfer-form__radio-group">
              <label className="transfer-form__radio-label">
                <input 
                  type="radio" 
                  name="returnToService" 
                  value="transfer" 
                  checked={formData.returnToServiceOption === 'transfer'}
                  onChange={() => setFormData({...formData, returnToServiceOption: 'transfer'})}
                  className="transfer-form__radio"
                />
                <span className="transfer-form__radio-custom"></span>
                {t('equipmentTable.modals.transfer.transferAllReturnToServiceInspections')}
              </label>
            </div>
          </div>

          <div className="transfer-form__section">
            <h3 className="transfer-form__section-title">{t('equipmentTable.modals.transfer.equipmentHasOpenInspections')}</h3>
            <div className="transfer-form__radio-group">
              <label className="transfer-form__radio-label">
                <input 
                  type="radio" 
                  name="openInspections" 
                  value="transfer" 
                  checked={formData.openInspectionsOption === 'transfer'}
                  onChange={() => setFormData({...formData, openInspectionsOption: 'transfer'})}
                  className="transfer-form__radio"
                />
                <span className="transfer-form__radio-custom"></span>
                {t('equipmentTable.modals.transfer.transferOpenInspections')}
              </label>
              <label className="transfer-form__radio-label">
                <input 
                  type="radio" 
                  name="openInspections" 
                  value="convert" 
                  checked={formData.openInspectionsOption === 'convert'}
                  onChange={() => setFormData({...formData, openInspectionsOption: 'convert'})}
                  className="transfer-form__radio"
                />
                <span className="transfer-form__radio-custom"></span>
                {t('equipmentTable.modals.transfer.convertOpenInspections')}
              </label>
            </div>
          </div>
        </>
      )}

      {/* Show warning when FBO is selected and revision codes don't match */}
      {formData?.selectedFBO && formData?.selectedFBORevision !== equipment?.revision_code && (
        <div className="transfer-form__warning">
          <div className="transfer-form__warning-icon">⚠️</div>
          <div className="transfer-form__warning-text">
              <h4>{t('equipmentTable.modals.transfer.inspectionRevisionMismatch')}</h4>
            <p>{t('equipmentTable.modals.transfer.destinationFboHasDifferentInspectionsRevision')}</p>
          </div>
        </div>
      )}
    </div>
  );
};